import React from 'react';
import { BulbFilled, BulbOutlined, DesktopOutlined } from '@ant-design/icons';
import { Alert, Tooltip } from 'antd';
import { useTheme } from '../../../../context/ThemeContext';
import logoImage from '../../../../images/klickly-logo-color.svg';
import { AuthAmbientBackdrop } from './AuthAmbientBackdrop';
import './index.scss';

// Compact, pill-shaped light/dark/system switch shown in the corner of every
// auth page. We keep it inline (rather than reusing the header dropdown) so
// pre-auth users get a one-tap toggle without an extra menu, while still
// letting power-users opt back into "follow system".
const AuthThemeToggle = () => {
    const { mode, setMode } = useTheme();
    const options = [
        { key: 'light', label: 'Light theme', icon: <BulbFilled aria-hidden='true' /> },
        { key: 'dark', label: 'Dark theme', icon: <BulbOutlined aria-hidden='true' /> },
        { key: 'system', label: 'Match system', icon: <DesktopOutlined aria-hidden='true' /> }
    ];

    return (
        <div className='auth-theme-toggle' role='group' aria-label='Theme'>
            {options.map((option) => (
                <Tooltip key={option.key} title={option.label} placement='bottom'>
                    <button
                        type='button'
                        className={`auth-theme-toggle__btn ${
                            mode === option.key ? 'auth-theme-toggle__btn--active' : ''
                        }`}
                        aria-label={option.label}
                        aria-pressed={mode === option.key}
                        onClick={() => setMode(option.key)}
                    >
                        {option.icon}
                    </button>
                </Tooltip>
            ))}
        </div>
    );
};

export const AuthLayout = ({
    children,
    mainBoxClass = '',
    error = '',
    eyebrow = 'Klickly Admin',
    heading = 'Sign in to continue',
    subheading = 'Internal tools for the Klickly team.',
    footer = null
}) => {
    const isWide = mainBoxClass.includes('mfa-setup');
    return (
        <div className='auth-page'>
            <AuthAmbientBackdrop />
            <AuthThemeToggle />
            <div className={`auth-card ${isWide ? 'auth-card--wide' : ''} ${mainBoxClass}`}>
                <div className='auth-card__accent' aria-hidden='true' />
                <header className='auth-card__header'>
                    <img className='auth-card__logo' src={logoImage} alt='Klickly' />
                    <div className='auth-card__eyebrow'>{eyebrow}</div>
                    <h1 className='auth-card__heading'>{heading}</h1>
                    {subheading ? <p className='auth-card__subheading'>{subheading}</p> : null}
                </header>
                <div className='auth-card__body'>
                    {error ? <Alert className='auth-error' message={error} type='error' showIcon role='alert' /> : null}
                    {children}
                </div>
                {footer ? <div className='auth-card__footer'>{footer}</div> : null}
            </div>
        </div>
    );
};
